/**
 * The headline numbers.
 *
 * A KPI row of stat tiles, not a chart: these are five single current values,
 * and a bar chart of five unrelated quantities would be a chart of nothing.
 * Network hashrate leads as the hero figure because it is the one number that
 * answers "how hard is this to win right now".
 *
 * Under four of them sits the same value's recent history, because a number on
 * its own cannot say whether it is high. Height gets none: it only ever goes
 * up, and a straight diagonal is a line with nothing to report — which is why
 * it sits last, so the four sparklines run uninterrupted rather than being
 * split by a tile with an empty slot.
 *
 * The series is fetched rather than pushed, and refetched only when the chain
 * crosses a bucket boundary — the one moment a point can change. Everything
 * else on this page arrives on the socket; fifty buckets per block to every
 * open tab would not.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ChainSeries } from '../api/generated/ChainSeries'
import type { ChainSeriesPoint } from '../api/generated/ChainSeriesPoint'
import type { ChainSummary } from '../api/generated/ChainSummary'
import type { Window as WindowName } from '../api/generated/Window'
import { fetchSeries } from '../api/rest'
import { bigNumber, clock, hashrate, height, seconds, windowSpan } from '../lib/format'
import { Sparkline, type SparkPoint } from './Sparkline'

/** Kept in step with `Window::blocks()` in the entities crate. */
const WINDOW_BLOCKS: Record<WindowName, number> = {
  '600': 600,
  '3600': 3600,
  '14400': 14400,
  '100800': 100800,
}

function Stat({
  label,
  value,
  note,
  hero,
  noteClass,
  title,
  trend,
}: {
  label: string
  value: string
  note?: string
  hero?: boolean
  noteClass?: string
  title?: string
  trend?: React.ReactNode
}) {
  return (
    <div className={hero ? 'stat stat-hero' : 'stat'}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" title={title}>
        {value}
      </div>
      <div className={noteClass ? `stat-note ${noteClass}` : 'stat-note'}>{note ?? ''}</div>
      {trend ?? <div className="spark" />}
    </div>
  )
}

/**
 * A tile whose note doubles as the sparkline's readout.
 *
 * A floating tooltip per tile would put five overlays in a row a hundred and
 * forty pixels wide. Borrowing the caption line instead keeps the number in the
 * place the reader is already looking, costs no layout, and falls back to the
 * caption the moment the pointer leaves.
 */
export function TrendStat({
  label,
  value,
  note,
  hero,
  noteClass,
  title,
  points,
}: {
  label: string
  value: string
  note: string
  hero?: boolean
  noteClass?: string
  title?: string
  points: SparkPoint[] | null
}) {
  const [probed, setProbed] = useState<number | null>(null)
  const onProbe = useCallback((index: number | null) => setProbed(index), [])
  const reading = probed !== null ? points?.[probed]?.label : undefined

  return (
    <Stat
      label={label}
      value={value}
      note={reading ?? note}
      {...(hero ? { hero } : {})}
      {...(reading ? { noteClass: 'reading' } : noteClass ? { noteClass } : {})}
      {...(title ? { title } : {})}
      {...(points ? { trend: <Sparkline points={points} label={label} onProbe={onProbe} /> } : {})}
    />
  )
}

/** When a point was, in the shortest form that still places it. */
function when(at: string | null, spanSeconds: number): string {
  return at ? clock(new Date(at).getTime(), spanSeconds * 1000) : ''
}

export function StatBar({
  summary,
  windowName,
}: {
  summary: ChainSummary | null
  windowName: WindowName
}) {
  const chain = summary?.chain ?? null
  const windowBlocks = WINDOW_BLOCKS[windowName]
  const bucketBlocks = windowBlocks / 50
  // The identity of the newest bucket. A new block only changes the chart when
  // it opens a new one, so this is the whole refetch condition.
  const bucket = Math.floor((summary?.height ?? 0) / bucketBlocks)
  const [series, setSeries] = useState<ChainSeries | null>(null)

  useEffect(() => {
    if (!chain) return
    const controller = new AbortController()
    fetchSeries(chain, windowName, controller.signal)
      .then(setSeries)
      .catch(() => {
        // The tiles are the point and they come from the socket; history that
        // will not load is a missing sparkline, never a broken page.
        if (!controller.signal.aborted) setSeries(null)
      })
    return () => controller.abort()
  }, [chain, windowName, bucket])

  const interval = summary
    ? (summary.block_interval_seconds ?? summary.target_block_time_seconds)
    : 6
  const spanSeconds = windowBlocks * interval

  // One pass over the points per tile, memoised: these rebuild only when the
  // series does, not on every hover.
  const trends = useMemo(() => {
    const points = series?.points ?? null
    if (!points || points.length === 0) return null
    const build = (
      pick: (p: ChainSeriesPoint) => number | null,
      say: (v: number) => string,
    ): SparkPoint[] =>
      points.map((p) => {
        const value = pick(p)
        const at = when(p.at, spanSeconds)
        return {
          value,
          label: value === null ? 'no data' : at ? `${say(value)} · ${at}` : say(value),
        }
      })

    return {
      hashrate: build(
        (p) => p.network_hashrate,
        (v) => hashrate(v),
      ),
      difficulty: build(
        (p) => {
          if (!p.difficulty) return null
          // Difficulty crosses the wire as a decimal string so it is never
          // rounded in transit. Plotting is the one place rounding it is
          // right: a sparkline is a shape, and a float carries the magnitude.
          const n = Number(p.difficulty)
          return Number.isFinite(n) ? n : null
        },
        (v) => bigNumber(BigInt(Math.round(v)).toString()),
      ),
      interval: build(
        (p) => p.block_interval_seconds,
        (v) => seconds(v),
      ),
      miners: build(
        (p) => p.miners,
        (v) => `${v} miner${v === 1 ? '' : 's'}`,
      ),
    }
  }, [series, spanSeconds])

  if (!summary) {
    return (
      <div className="stats">
        {['Network hashrate', 'Difficulty', 'Block time', 'Miners', 'Height'].map((label) => (
          <Stat key={label} label={label} value="—" />
        ))}
      </div>
    )
  }

  return (
    <div className="stats">
      <TrendStat
        hero
        label="Network hashrate"
        value={hashrate(summary.network_hashrate)}
        // Presenting an estimate as a measurement is the easiest way to lie
        // with this number, so the tile says which it is every time.
        note={summary.hashrate_from_target ? 'nominal — from target block time' : 'measured at tip'}
        {...(summary.hashrate_from_target ? { noteClass: 'nominal' } : {})}
        points={trends?.hashrate ?? null}
      />
      <TrendStat
        label="Difficulty"
        value={bigNumber(summary.difficulty)}
        note="expected hashes per block"
        {...(summary.difficulty ? { title: summary.difficulty } : {})}
        points={trends?.difficulty ?? null}
      />
      <TrendStat
        label="Block time"
        value={seconds(summary.block_interval_seconds)}
        note={`target ${seconds(summary.target_block_time_seconds)}`}
        points={trends?.interval ?? null}
      />
      <TrendStat
        label="Miners"
        value={String(summary.distinct_miners)}
        note={
          summary.telemetry_nodes !== null
            ? `${summary.telemetry_nodes} node${summary.telemetry_nodes === 1 ? '' : 's'} on telemetry`
            : `winning in ${windowSpan(windowBlocks, interval)}`
        }
        points={trends?.miners ?? null}
      />
      <Stat label="Height" value={height(summary.height)} note="best block seen" />
    </div>
  )
}
